import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useMutation, useQuery, gql } from "@apollo/client";
import { format } from "date-fns";

const ADD_COORDINATED_CARE_NOTE = gql`
  mutation AddCoordinatedCareNote($odrnumber: String!, $participant: String!, $note: AddCoordinatedCareNoteInput!) {
    addCoordinatedCareNote(odrnumber: $odrnumber, participant: $participant, note: $note) {
      message
    }
  }
`;

// Query to fetch Place of Service options from database
const GET_POS_OPTIONS = gql`
  query GetCalaimPosOptions {
    calaimPosOptions {
      pos_code
      pos_name
    }
  }
`;

// Query to fetch Procedure Type options from database
const GET_PROCEDURE_TYPES = gql`
  query GetCalaimProcedureTypes {
    calaimProcedureTypes {
      value
      label
    }
  }
`;

// Hardcoded procedure types from billing_proceduretypes table
const PROCEDURE_TYPE_OPTIONS = [
  { value: "Initial Assessment-first 90 days", label: "Initial Assessment-first 90 days" },
  { value: "Care Plan-every quarter or more", label: "Care Plan-every quarter or more" },
  { value: "Client Contact", label: "Client Contact" },
  { value: "Client Outreach", label: "Client Outreach" },
  { value: "Care Coordination", label: "Care Coordination" },
];

const NOTE_TYPE_OPTIONS = [
  { value: "ecm", label: "ECM Note" },
  { value: "coordinated_care", label: "Coordinated Care Note" }
];

const CARE_PHASE_OPTIONS = [
  { value: "enrolled", label: "Enrolled" },
  { value: "pre_enrollment_outreach", label: "Pre-Enrollment Outreach" }
];

const SERVICE_TYPE_OPTIONS = [
  { value: "none", label: "-- Select type --" },
  { value: "Housing", label: "Housing" },
  { value: "Community", label: "Community" },
  { value: "Pre-release", label: "Pre-release" },
  { value: "Psychiatry", label: "Psychiatry" },
  { value: "Nursing", label: "Nursing" }
];

const PLACE_OF_SERVICE_OPTIONS = [
  { value: "none", label: "-- Select an option --" },
  { value: "home", label: "Home" },
  { value: "office", label: "Office" },
  { value: "telehealth_home", label: "Telehealth in pt home" },
  { value: "telehealth_outside", label: "Telehealth outside pt home" }
];

const POS_NAME_TO_PLACE_OF_SERVICE: Record<string, string> = {
  "Home": "home",
  "Office": "office",
  "Telehealth in pt home": "telehealth_home",
  "Telehealth outside pt home": "telehealth_outside"
};

const resolvePlaceOfService = (
  posCode: string,
  posOptions?: { pos_code: string; pos_name: string }[]
) => {
  if (!posCode) {
    return "";
  }

  if (posCode === "0") {
    return "";
  }

  if (!posOptions) {
    return "";
  }

  const matchedPos = posOptions.find((option) => option.pos_code === posCode);
  if (!matchedPos) {
    return "";
  }

  return POS_NAME_TO_PLACE_OF_SERVICE[matchedPos.pos_name] || "";
};

type NoteType = "ecm" | "coordinated_care";

interface AddNoteCalAIMProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    name: string;
    username: string;
  };
  calaimData: {
    fin: string;
  };
  odrNumber: string;
  participant: string;
  refetch: () => void;
}

export const AddNoteCalAIM: React.FC<AddNoteCalAIMProps> = ({
  isOpen,
  onClose,
  user,
  calaimData,
  odrNumber,
  participant,
  refetch
}) => {
  // Fetch Place of Service options
  const { data: posData, loading: posLoading } = useQuery(GET_POS_OPTIONS);
  
  // Fetch Procedure Type options
  const { data: procedureTypeData, loading: procedureTypeLoading } = useQuery(GET_PROCEDURE_TYPES);

  const [noteType, setNoteType] = useState<NoteType>("ecm");
  const [formData, setFormData] = useState({
    service_type: "",
    place_of_service: "",
    calaim_pos: "0",
    proceduremod1: "",
    proceduremod2: "",
    care_phase: "enrolled",
    proceduretype: "none",
    procedurelength: "15",
    note_entry_date: format(new Date(), "yyyy-MM-dd"),
    procedurenote: "",
    procedureentery: user?.name || user?.username || "",
    fin: calaimData?.fin || "",
    isMdtConference: false
  });

  const [addCoordinatedCareNote, { loading }] = useMutation(ADD_COORDINATED_CARE_NOTE);
  
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const isEcm = noteType === "ecm";
  const isCoordinatedCare = noteType === "coordinated_care";

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNoteTypeChange = (value: string) => {
    setNoteType(value as NoteType);
    setErrors({});
  };

  const handleSelectChange = (name: string, value: string) => {
    if (name === "isMdtConference") {
      const isMdtConference = value === "true";
      setFormData((prev) => ({ 
        ...prev, 
        [name]: isMdtConference,
        // Clear modifier selections when MDT Conference is checked
        ...(isMdtConference && {
          proceduremod1: "",
          proceduremod2: ""
        })
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (isEcm) {
      const requiresTelehealthPOS = !formData.isMdtConference && formData.proceduremod1 === "0";
      if (requiresTelehealthPOS && formData.calaim_pos === "0") {
        newErrors.calaim_pos = "Place of Service must be selected";
      }

      // Skip modifier validation if MDT Conference is selected
      if (!formData.isMdtConference) {
        if (!formData.proceduremod1) {
          newErrors.proceduremod1 = "In Person/Telehealth must be selected";
        }

        if (!formData.proceduremod2) {
          newErrors.proceduremod2 = "Clinical/NonClinical must be selected";
        }
      }
    }

    if (isCoordinatedCare) {
      if (!formData.service_type || formData.service_type === "none") {
        newErrors.service_type = "Service Type must be selected";
      }

      if (!formData.place_of_service || formData.place_of_service === "none") {
        newErrors.place_of_service = "Place of Service must be selected";
      }

      if (!formData.procedurelength) {
        newErrors.procedurelength = "Procedure length must be selected";
      }

      if (!formData.procedurenote.trim()) {
        newErrors.procedurenote = "Procedure note is required";
      }

      if (!formData.procedureentery) {
        newErrors.procedureentery = "Entered by must be filled";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const notePayload: Record<string, any> = {};

      if (isEcm) {
        const posCode = formData.calaim_pos === "0" ? "" : formData.calaim_pos;
        const placeOfService = resolvePlaceOfService(posCode, posData?.calaimPosOptions);

        notePayload.service_type = "ECM";
        notePayload.care_phase = formData.care_phase;
        notePayload.place_of_service = placeOfService;
        notePayload.pos_code = posCode;
        notePayload.proceduremod1 = formData.isMdtConference ? null : formData.proceduremod1;
        notePayload.proceduremod2 = formData.isMdtConference ? null : formData.proceduremod2;
        notePayload.proceduretype = formData.proceduretype;
        notePayload.isMdtConference = formData.isMdtConference;
        notePayload.procedurelength = formData.procedurelength;
        notePayload.note_entry_date = formData.note_entry_date;
        notePayload.procedurenote = formData.procedurenote;
        notePayload.procedureentery = formData.procedureentery;
      }

      if (isCoordinatedCare) {
        notePayload.service_type = formData.service_type;
        notePayload.place_of_service = formData.place_of_service;
        notePayload.procedurelength = formData.procedurelength;
        notePayload.note_entry_date = formData.note_entry_date;
        notePayload.procedurenote = formData.procedurenote;
        notePayload.procedureentery = formData.procedureentery;
      }

      await addCoordinatedCareNote({
        variables: {
          odrnumber: odrNumber,
          participant: participant,
          note: notePayload,
        },
      });
      refetch();
      onClose();
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const dialogTitle = noteType === "coordinated_care"
    ? "Add New Coordinated Care Note"
    : "Add New ECM Note";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-none max-h-[90vh] overflow-y-auto" style={{ width: '90vw', minWidth: '1200px' }}>
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold">
            {dialogTitle}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="w-1/3 min-w-[250px]">
            <Label className="mb-2 block">Entry Type</Label>
            <Select value={noteType} onValueChange={handleNoteTypeChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select entry type..." />
              </SelectTrigger>
              <SelectContent>
                {NOTE_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isEcm && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="calaim_pos">Place of Service</Label>
                  <Select 
                    onValueChange={(value) => handleSelectChange("calaim_pos", value)}
                    value={formData.calaim_pos}
                  >
                    <SelectTrigger className={errors.calaim_pos ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select place of service" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">-- Select an option --</SelectItem>
                      {posLoading ? (
                        <SelectItem value="loading">Loading...</SelectItem>
                      ) : (
                        posData?.calaimPosOptions
                          ?.filter((option: { pos_code: string; pos_name: string }) => 
                            option.pos_name === "Home" || 
                            option.pos_name === "Office" ||
                            option.pos_name === "Telehealth in pt home" ||
                            option.pos_name === "Telehealth outside pt home"
                          )
                          ?.map((option: { pos_code: string; pos_name: string }) => (
                            <SelectItem key={option.pos_code} value={option.pos_code}>
                              {option.pos_name}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                  {errors.calaim_pos && <p className="text-sm text-red-500">{errors.calaim_pos}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="note_entry_date">Note Date</Label>
                  <Input
                    type="date"
                    id="note_entry_date"
                    name="note_entry_date"
                    value={formData.note_entry_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isMdtConference"
                    checked={formData.isMdtConference}
                    onCheckedChange={(checked) => handleSelectChange("isMdtConference", checked ? "true" : "false")}
                  />
                  <Label htmlFor="isMdtConference" className="font-medium">
                    Multidisciplinary Team Conference (G9007)
                  </Label>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>In Person/Telehealth</Label>
                  <RadioGroup 
                    value={formData.proceduremod1} 
                    onValueChange={(value) => handleSelectChange("proceduremod1", value)}
                    className={errors.proceduremod1 ? "border border-red-500 rounded-md p-2" : ""}
                    disabled={formData.isMdtConference}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1" id="inperson" />
                      <Label htmlFor="inperson">In Person</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="0" id="telehealth" />
                      <Label htmlFor="telehealth">Telehealth</Label>
                    </div>
                  </RadioGroup>
                  {errors.proceduremod1 && <p className="text-sm text-red-500">{errors.proceduremod1}</p>}
                </div>

                <div className="space-y-2">
                  <Label>Clinical/NonClinical</Label>
                  <RadioGroup 
                    value={formData.proceduremod2} 
                    onValueChange={(value) => handleSelectChange("proceduremod2", value)}
                    className={errors.proceduremod2 ? "border border-red-500 rounded-md p-2" : ""}
                    disabled={formData.isMdtConference}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="1" id="clinical" />
                      <Label htmlFor="clinical">Clinical</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="0" id="nonclinical" />
                      <Label htmlFor="nonclinical">NonClinical</Label>
                    </div>
                  </RadioGroup>
                  {errors.proceduremod2 && <p className="text-sm text-red-500">{errors.proceduremod2}</p>}
                </div>

              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Procedure Type</Label>
                  <Select
                    onValueChange={(value) => handleSelectChange("proceduretype", value)}
                    value={formData.proceduretype}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select procedure type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select an option --</SelectItem>
                      {PROCEDURE_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Member Status</Label>
                  <Select
                    onValueChange={(value) => handleSelectChange("care_phase", value)}
                    value={formData.care_phase}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select member status" />
                    </SelectTrigger>
                    <SelectContent>
                      {CARE_PHASE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Procedure Length</Label>
                  <Select
                    onValueChange={(value) => handleSelectChange("procedurelength", value)}
                    value={formData.procedurelength}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select procedure length" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCEDURE_LENGTH_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="procedurenote">Procedure Note</Label>
                <Textarea
                  id="procedurenote"
                  name="procedurenote"
                  value={formData.procedurenote}
                  onChange={handleInputChange}
                  placeholder="Enter your note here"
                  className="min-h-[100px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="procedureentery">Entered By</Label>
                <Input
                  id="procedureentery"
                  name="procedureentery"
                  value={formData.procedureentery}
                  className="bg-gray-100"
                  readOnly
                  required
                />
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? "Saving..." : "Save Note"}
                </Button>
              </div>
            </>
          )}

          {isCoordinatedCare && (
            <>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="service_type">Service Type</Label>
                  <Select 
                    onValueChange={(value) => handleSelectChange("service_type", value)}
                    value={formData.service_type}
                  >
                    <SelectTrigger className={errors.service_type ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select service type" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.service_type && <p className="text-sm text-red-500">{errors.service_type}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="place_of_service">Place of Service</Label>
                  <Select 
                    onValueChange={(value) => handleSelectChange("place_of_service", value)}
                    value={formData.place_of_service}
                  >
                    <SelectTrigger className={errors.place_of_service ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select place of service" />
                    </SelectTrigger>
                    <SelectContent>
                      {PLACE_OF_SERVICE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.place_of_service && <p className="text-sm text-red-500">{errors.place_of_service}</p>}
                </div>
                
                <div className="space-y-2">
                  <Label>Procedure Length</Label>
                  <Select
                    onValueChange={(value) => handleSelectChange("procedurelength", value)}
                    value={formData.procedurelength}
                  >
                    <SelectTrigger className={errors.procedurelength ? "border-red-500" : ""}>
                      <SelectValue placeholder="Select procedure length" />
                    </SelectTrigger>
                    <SelectContent>
                      {PROCEDURE_LENGTH_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.procedurelength && <p className="text-sm text-red-500">{errors.procedurelength}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="note_entry_date">Note Date</Label>
                  <Input
                    type="date"
                    id="note_entry_date"
                    name="note_entry_date"
                    value={formData.note_entry_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="procedureentery">Entered By</Label>
                  <Input
                    id="procedureentery"
                    name="procedureentery"
                    value={formData.procedureentery}
                    className={errors.procedureentery ? "border-red-500" : ""}
                    readOnly
                    required
                  />
                  {errors.procedureentery && <p className="text-sm text-red-500">{errors.procedureentery}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="procedurenote">Procedure Note</Label>
                <Textarea
                  id="procedurenote"
                  name="procedurenote"
                  value={formData.procedurenote}
                  onChange={handleInputChange}
                  placeholder="Enter your note here"
                  className={`min-h-[100px] ${errors.procedurenote ? "border-red-500" : ""}`}
                  required
                />
                {errors.procedurenote && <p className="text-sm text-red-500">{errors.procedurenote}</p>}
              </div>

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={onClose} type="button">
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? "Saving..." : "Save Note"}
                </Button>
              </div>
            </>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

// These match the procedure lengths from the PHP file
const PROCEDURE_LENGTH_OPTIONS = [
  { value: "15", label: "15 mins" },
  { value: "30", label: "30 mins" },
  { value: "45", label: "45 mins" },
  { value: "60", label: "60 mins" },
  { value: "75", label: "1hour 15mins" },
  { value: "90", label: "1hour 30mins" },
  { value: "105", label: "1hour 45mins" },
  { value: "120", label: "2hours" },
  { value: "135", label: "2hours 15mins" },
  { value: "150", label: "2hours 30mins" },
  { value: "165", label: "2hours 45mins" },
  { value: "180", label: "3hours" },
  { value: "195", label: "3hours 15mins" },
  { value: "210", label: "3hours 30mins" },
  { value: "225", label: "3hours 45mins" },
  { value: "240", label: "4hours" }
];
